import React from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { MemoryRouter } from 'react-router-dom';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { render as rtlRender } from '@testing-library/react';

import authSlice from '../state/slices/authSlice';
import notesSlice from '../state/slices/notesSlice';
import userSlice from '../state/slices/userSlice';
import waitlistPreferencesSlice from '../state/slices/waitlistPreferencesSlice';
import waitlistSlice from '../state/slices/waitlistSlice';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const theme = extendTheme({
  colors: {
    blue: {
      50: '#ebf8ff',
      500: '#3182ce',
      600: '#2c5aa0',
    },
    gray: {
      50: '#f7fafc',
      100: '#edf2f7',
      500: '#718096',
      600: '#4a5568',
    },
  },
  components: {
    FormLabel: {
      baseStyle: {
        fontSize: '11px',
      },
    },
  },
});

// Test store setup function
export const setupTestStore = (preloadedState?: any) => {
  const rootReducer = combineReducers({
    auth: authSlice,
    user: userSlice,
    waitlist: waitlistSlice,
    notes: notesSlice,
    waitlistPreferences: waitlistPreferencesSlice,
  });

  return configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }),
  });
};

export type TestRenderOptions = {
  /** Set true when the tree under test provides its own router (e.g. RouterProvider). */
  omitRouterWrapper?: boolean;
};

function render(
  component: React.ReactElement,
  store = setupTestStore(),
  options?: TestRenderOptions,
) {
  const inner = options?.omitRouterWrapper ? component : <MemoryRouter>{component}</MemoryRouter>;
  return rtlRender(
    <QueryClientProvider client={queryClient}>
      <ChakraProvider theme={theme}>
        <ReduxProvider store={store}>{inner}</ReduxProvider>
      </ChakraProvider>
    </QueryClientProvider>,
  );
}

export * from '@testing-library/react';

export { render }; 